import { FC, ReactNode } from 'react';

import Field from './Field';
import type { InputProps } from './Inputs';

export type CurrencyInputProps = Omit<InputProps, 'type'> & {
  /** The symbol shown before the field. Defaults to "$". */
  currency?: string;
  /**
   * A page hook on the real input. `className` places the wrapper; this names
   * the control the spec drives with `.fill()`.
   */
  inputClassName?: string;
};

export type CurrencyFieldProps = Omit<InputProps, 'type' | 'className' | 'invalid' | 'inputClassName'> & {
  label: string;
  currency?: string;
  description?: ReactNode;
  /** Same contract as Field: shown instead of the description. */
  error?: ReactNode;
  id?: string;
  /** Grid placement: take the whole row of a FormGrid. */
  span?: 'full';
  /** Placement only: the frame and focus are the wrapper's. */
  className?: string;
  /** A page hook on the real input. See CurrencyInput. */
  inputClassName?: string;
};

/**
 * A decimal text input with a currency symbol.
 *
 * The wrapper, not the input, draws the height, border and focus so the control
 * reads like every other control. The input keeps the shared classes and
 * forwards every native attribute, so a Field clones its id,
 * aria-describedby and aria-invalid straight onto the real input.
 */
export const CurrencyInput: FC<CurrencyInputProps> = ({ currency = '$', invalid, className, inputClassName, ...rest }) => {
  // Field injects aria-invalid rather than invalid, so treat either as the
  // error state and the frame goes red without the page repeating itself.
  const isInvalid = invalid ?? (rest['aria-invalid'] === true || rest['aria-invalid'] === 'true');
  return (
    <span className={['ui-currency', isInvalid ? 'ui-control--invalid' : undefined, className].filter(Boolean).join(' ')}>
      <span className="ui-currency-prefix" aria-hidden="true">
        {currency}
      </span>
      <input type="text" inputMode="decimal" {...rest} className={['ui-input', 'ui-control', inputClassName].filter(Boolean).join(' ')} />
    </span>
  );
};

/** A labelled currency control. */
const CurrencyField: FC<CurrencyFieldProps> = ({ label, currency, description, error, id, span, className, inputClassName, ...rest }) => (
  <Field label={label} id={id} description={description} error={error} span={span} className={className}>
    <CurrencyInput currency={currency} invalid={Boolean(error)} inputClassName={inputClassName} {...rest} />
  </Field>
);

export default CurrencyField;
